import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Modal,
  Stack,
  Text,
  Radio,
  Group,
  Badge,
  Select,
  Button,
  Loader,
  Center,
  Divider,
  Alert,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useQuery } from "@tanstack/react-query";
import { IconAlertCircle, IconDeviceDesktop } from "@tabler/icons-react";
import {
  bridgeService,
  type Workstation,
  type Device,
} from "../../services/bridgeService";
import {
  workstationPreferenceService,
  type BridgeDeviceType,
} from "../../services/workstationPreference.service";
import { deviceBridgeService } from "../../services/deviceBridge.service";
import { slideLabelPrinterService } from "../../services/slideLabelPrinter.service";
import { cassetteLabelPrinterService } from "../../services/cassetteLabelPrinter.service";
import { documentScanningService } from "../../services/documentScanning.service";
import { stickerPrinterService } from "../../services/stickerPrinter.service";
import PreferredDeviceSelection from "./PreferredDeviceSelection";
import { usePreferredPrinterSelection } from "../../hooks/usePreferredPrinterSelection";

interface Props {
  opened: boolean;
  onClose: () => void;
}

const DEVICE_TYPE_LABELS: Record<BridgeDeviceType, string> = {
  slide_printer: "Slide Printer",
  cassette_printer: "Cassette Printer",
  sticker_printer: "Sticker Printer",
  document_scanner: "Document Scanner",
};

const ALL_DEVICE_TYPES: BridgeDeviceType[] = [
  "slide_printer",
  "cassette_printer",
  "sticker_printer",
  "document_scanner",
];

function selectDevicesForWorkstation(
  devices: Device[],
  previous: Record<BridgeDeviceType, string | null>
): Record<BridgeDeviceType, string | null> {
  const selections = Object.fromEntries(
    ALL_DEVICE_TYPES.map((type) => [type, null])
  ) as Record<BridgeDeviceType, string | null>;
  ALL_DEVICE_TYPES.forEach((type) => {
    const matching = devices.filter((device) => device.deviceType === type);
    const previousId = previous[type];
    const previousStillExists = matching.some(
      (device) => device.id === previousId
    );
    selections[type] = previousStillExists
      ? previousId
      : matching[0]?.id ?? null;
  });
  return selections;
}

const WorkstationSelectorModal: React.FC<Props> = ({ opened, onClose }) => {
  const currentWsId = workstationPreferenceService.getSelectedWorkstationId();

  const [selectedWsId, setSelectedWsId] = useState<string | null>(currentWsId);
  const [deviceSelections, setDeviceSelections] = useState<
    Record<BridgeDeviceType, string | null>
  >({
    slide_printer:
      workstationPreferenceService.getSelectedDeviceId("slide_printer"),
    cassette_printer:
      workstationPreferenceService.getSelectedDeviceId("cassette_printer"),
    sticker_printer:
      workstationPreferenceService.getSelectedDeviceId("sticker_printer"),
    document_scanner:
      workstationPreferenceService.getSelectedDeviceId("document_scanner"),
  });
  const preferredPrinters = usePreferredPrinterSelection(opened);

  // Fetch all workstations
  const { data: workstationsData, isLoading: wsLoading } = useQuery({
    queryKey: ["bridge-workstations-selector"],
    queryFn: () => bridgeService.getWorkstations({ limit: 100 }),
    select: (res) => res.data,
    enabled: opened,
  });

  const workstations = useMemo(
    () => workstationsData?.data || [],
    [workstationsData]
  );

  // Fetch devices for selected workstation
  const { data: devicesData, isLoading: devicesLoading } = useQuery({
    queryKey: ["bridge-devices-selector", selectedWsId],
    queryFn: () => bridgeService.getDevices(selectedWsId!),
    select: (res) => res.data,
    enabled: !!selectedWsId && opened,
  });

  const devices = useMemo(() => devicesData?.data || [], [devicesData]);

  // Auto-select devices when workstation changes
  useEffect(() => {
    if (!devices.length) return;
    setDeviceSelections((previous) =>
      selectDevicesForWorkstation(devices, previous)
    );
  }, [devices]);

  const handlePreferredCassetteChange = useCallback(
    (deviceId: string | null) => {
      preferredPrinters.setCassettePrinterId(deviceId);
    },
    [preferredPrinters]
  );

  const handlePreferredSlideChange = useCallback(
    (deviceId: string | null) => {
      preferredPrinters.setSlidePrinterId(deviceId);
    },
    [preferredPrinters]
  );

  const handleWorkstationChange = useCallback((workstationId: string) => {
    setSelectedWsId(workstationId || null);
  }, []);

  const handleSave = useCallback(() => {
    if (!selectedWsId) {
      notifications.show({
        title: "Select a workstation",
        message: "Please select a workstation to continue",
        color: "orange",
      });
      return;
    }

    const ws = workstations.find((w: Workstation) => w.id === selectedWsId);
    if (!ws) return;

    let printerPreferencesSaved = false;
    try {
      printerPreferencesSaved = preferredPrinters.savePreferences();
    } catch {
      notifications.show({
        title: "Printer preferences not saved",
        message:
          "Choose an available printer, or clear the unavailable selection and try again.",
        color: "red",
      });
      return;
    }

    // Save workstation preference
    workstationPreferenceService.setSelectedWorkstation(selectedWsId, ws.name);

    // Update local bridge port and reconnect so the frontend uses the right port
    deviceBridgeService.setLocalPort(ws.localPort);
    deviceBridgeService.forceReconnect();

    // Save device preferences and update services
    ALL_DEVICE_TYPES.forEach((type) => {
      const deviceId = deviceSelections[type];
      if (deviceId) {
        const device = devices.find((d: Device) => d.id === deviceId);
        if (device) {
          workstationPreferenceService.setDeviceForType(
            type,
            device.id,
            device.name
          );

          // Update the corresponding service
          if (type === "slide_printer") {
            slideLabelPrinterService.setDevice(device.id, device.name);
          } else if (type === "cassette_printer") {
            cassetteLabelPrinterService.setDevice(device.id, device.name);
          } else if (type === "document_scanner") {
            documentScanningService.setDevice(device.id, device.name);
          } else if (type === "sticker_printer") {
            stickerPrinterService.setDevice(device.id, device.name);
          }
        }
      } else {
        workstationPreferenceService.clearDeviceForType(type);
      }
    });

    notifications.show(
      printerPreferencesSaved
        ? {
            title: "Preferences saved",
            message: `Now using ${ws.name}`,
            color: "green",
          }
        : {
            title: "Workstation configured",
            message: `Now using ${ws.name}. Printer preferences were left unchanged because the printer list is unavailable.`,
            color: "yellow",
          }
    );

    onClose();
  }, [
    deviceSelections,
    devices,
    onClose,
    preferredPrinters,
    selectedWsId,
    workstations,
  ]);

  const selectedWs = useMemo(
    () => workstations.find((w: Workstation) => w.id === selectedWsId),
    [selectedWsId, workstations]
  );

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group gap="xs">
          <IconDeviceDesktop size={20} />
          <Text fw={600}>Printer &amp; Workstation Preferences</Text>
        </Group>
      }
      size="lg"
    >
      <Stack>
        <PreferredDeviceSelection
          cassettePrinterId={preferredPrinters.cassettePrinterId}
          slidePrinterId={preferredPrinters.slidePrinterId}
          cassettePrinterOptions={preferredPrinters.cassettePrinterOptions}
          slidePrinterOptions={preferredPrinters.slidePrinterOptions}
          isLoading={preferredPrinters.isLoading}
          hasLoadingError={preferredPrinters.isError}
          hasPartialLoadingError={preferredPrinters.hasPartialError}
          hasStaleCassettePrinter={preferredPrinters.hasStaleCassettePrinter}
          hasStaleSlidePrinter={preferredPrinters.hasStaleSlidePrinter}
          onCassettePrinterChange={handlePreferredCassetteChange}
          onSlidePrinterChange={handlePreferredSlideChange}
        />

        <Divider />

        <Text fw={600}>Select Workstation</Text>
        <Text size="sm" c="dimmed">
          Select the workstation you are working at. Devices on that workstation
          will be used for printing and scanning.
        </Text>

        {wsLoading ? (
          <Center py="md">
            <Loader size="sm" />
          </Center>
        ) : workstations.length === 0 ? (
          <Alert
            icon={<IconAlertCircle size={16} />}
            title="No workstations found"
            color="orange"
          >
            No bridge workstations are registered. Ask an administrator to
            register a workstation in the Bridge admin page.
          </Alert>
        ) : (
          <Radio.Group
            value={selectedWsId || ""}
            onChange={handleWorkstationChange}
          >
            <Stack gap="xs">
              {workstations.map((ws: Workstation) => (
                <Radio
                  key={ws.id}
                  value={ws.id}
                  label={
                    <Group gap="xs">
                      <Text size="sm" fw={500}>
                        {ws.name}
                      </Text>
                      <Badge
                        size="xs"
                        color={ws.status === "online" ? "green" : "gray"}
                      >
                        {ws.status === "online" ? "Online" : "Offline"}
                      </Badge>
                      {ws.deviceCount > 0 && (
                        <Text size="xs" c="dimmed">
                          {ws.onlineDeviceCount}/{ws.deviceCount} devices
                        </Text>
                      )}
                    </Group>
                  }
                />
              ))}
            </Stack>
          </Radio.Group>
        )}

        {selectedWsId && (
          <>
            <Divider label="Device Selection" labelPosition="center" />

            {devicesLoading ? (
              <Center py="sm">
                <Loader size="sm" />
              </Center>
            ) : devices.length === 0 ? (
              <Text size="sm" c="dimmed">
                No devices registered on{" "}
                {selectedWs?.name || "this workstation"}.
              </Text>
            ) : (
              <Stack gap="sm">
                {ALL_DEVICE_TYPES.map((type) => {
                  const matching = devices.filter(
                    (d: Device) => d.deviceType === type
                  );
                  if (matching.length === 0) return null;

                  return (
                    <Group key={type} justify="space-between" align="center">
                      <Text size="sm" fw={500} w={140}>
                        {DEVICE_TYPE_LABELS[type]}:
                      </Text>
                      <Group gap="xs" style={{ flex: 1 }}>
                        {matching.length === 1 ? (
                          <Stack gap={2}>
                            <Group gap="xs">
                              <Text size="sm">{matching[0].name}</Text>
                              <Badge
                                size="xs"
                                color={
                                  matching[0].status === "connected"
                                    ? "green"
                                    : "gray"
                                }
                              >
                                {matching[0].status}
                              </Badge>
                            </Group>
                            {matching[0].connectionType === "file_output" && (
                              <Text size="xs" c="dimmed">
                                Output: {matching[0].connectionAddress}
                              </Text>
                            )}
                          </Stack>
                        ) : (
                          <Select
                            size="xs"
                            placeholder="Select device"
                            data={matching.map((d: Device) => ({
                              value: d.id,
                              label: `${d.name} (${d.status})`,
                            }))}
                            value={deviceSelections[type]}
                            onChange={(val) =>
                              setDeviceSelections((prev) => ({
                                ...prev,
                                [type]: val,
                              }))
                            }
                            style={{ flex: 1 }}
                          />
                        )}
                      </Group>
                    </Group>
                  );
                })}
              </Stack>
            )}
          </>
        )}

        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            loading={wsLoading || devicesLoading || preferredPrinters.isLoading}
          >
            Save &amp; Use This Station
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default WorkstationSelectorModal;
